import React, { useState, useEffect, useCallback } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  useTheme, 
  Box, 
  LinearProgress, 
  alpha, 
  Collapse,
  IconButton, 
  Chip,
  Fade,
  Badge,
  Stack,
  Divider,
  Avatar,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PlaceIcon from '@mui/icons-material/Place';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useAuth } from '../../context/AuthContext';
import { analyticsApi } from '../../services/api';

// Color palettes for donut charts
const CHART_COLORS = {
  bridgeTypes: ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb', '#e3f2fd'],
  inspectionStatus: {
    'Inspected': '#4caf50',
    'Uninspected': '#f44336'
  }
};

// Helper functions to prepare chart data
const prepareBridgeTypeChartData = (bridgeTypeBreakdown) => {
  return bridgeTypeBreakdown.map((item, index) => ({
    name: item.subtype_name,
    value: Number(item.count) || 0,
    color: CHART_COLORS.bridgeTypes[index % CHART_COLORS.bridgeTypes.length]
  }));
};

const prepareRatingChartData = (ratingBreakdown) => {
  return ratingBreakdown.map(item => ({
    name: item.rating_name,
    value: Number(item.count) || 0,
    color: item.color_code || '#9e9e9e' // Fallback color
  }));
};

const prepareInspectionStatusChartData = (totalBridges, uninspectedCount) => {
  const inspectedCount = totalBridges - uninspectedCount;
  return [
    {
      name: 'Inspected',
      value: inspectedCount,
      color: CHART_COLORS.inspectionStatus.Inspected
    },
    {
      name: 'Uninspected', 
      value: uninspectedCount,
      color: CHART_COLORS.inspectionStatus.Uninspected
    }
  ].filter(item => item.value > 0);
};

// Function to generate bridge data from real stats
const getBridgeDataFromStats = (stats, loading) => {
  if (loading) {
    return {
      totalBridges: { id: 'total_bridges', title: "Total Bridges", count: '...', progress: 0, description: "Loading data...", slogan: "Infrastructure Assets", Icon: AccountBalanceIcon, color: 'primary.main', trend: '-', featured: true },
      bridgeTypes: { id: 'bridge_types', title: "Bridge Type Breakdown", count: '...', progress: 0, description: "Loading data...", Icon: CategoryIcon, color: 'info.main', trend: '-' },
      severeBridges: { id: 'severe_bridges', title: "Severe Rated Bridges", count: '...', progress: 0, description: "Loading data...", Icon: WarningAmberIcon, color: 'error.main', trend: '-' },
      uninspected: { id: 'uninspected_bridges', title: "Uninspected Bridges", count: '...', progress: 0, description: "Loading data...", Icon: AssignmentLateIcon, color: 'warning.main', trend: '-' },
      networkSevere: { id: 'network_severe', title: "Network Most Severe", count: '...', progress: 0, description: "Loading data...", Icon: PlaceIcon, color: 'secondary.main', trend: '-' },
      sectionSevere: { id: 'section_severe', title: "Section Most Severe", count: '...', progress: 0, description: "Loading data...", Icon: LocationOnIcon, color: 'secondary.main', trend: '-' },
    };
  }

  const totalBridges = stats.totalBridges || 0;
  const bridgeTypeCount = stats.bridgeTypeBreakdown?.length || 0;
  const severeRated = stats.severeRatedBridges || 0;
  const uninspected = stats.bridgesWithoutInspection || 0;
  const networkSevereCount = stats.networkWithMostSevereBridges?.severe_count || 0;
  const sectionSevereCount = stats.sectionWithMostSevereBridges?.severe_count || 0;

  return {
    totalBridges: { 
      id: 'total_bridges', 
      title: "Total Bridges", 
      count: totalBridges, 
      progress: totalBridges > 0 ? 100 : 0, 
      description: `Infrastructure assets across all networks`,
      Icon: AccountBalanceIcon, 
      color: 'primary.main', 
      trend: totalBridges > 0 ? `${totalBridges} bridges` : '0'
    },
    bridgeTypes: { 
      id: 'bridge_types', 
      title: "Bridge Type Breakdown", 
      count: bridgeTypeCount, 
      progress: bridgeTypeCount > 0 ? Math.min((bridgeTypeCount / 10) * 100, 100) : 0, 
      description: `${bridgeTypeCount} different bridge types`,
      Icon: CategoryIcon, 
      color: 'info.main', 
      trend: bridgeTypeCount > 0 ? `${bridgeTypeCount} types` : '0',
      breakdown: stats.bridgeTypeBreakdown,
      chartData: prepareBridgeTypeChartData(stats.bridgeTypeBreakdown || [])
    },
    severeBridges: { 
      id: 'severe_bridges', 
      title: "Bridge Rating Breakdown", 
      count: severeRated, 
      progress: totalBridges > 0 ? Math.round((severeRated / totalBridges) * 100) : 0, 
      description: `${severeRated} bridges need urgent attention`,
      Icon: WarningAmberIcon, 
      color: 'error.main', 
      trend: severeRated > 0 ? `${severeRated} critical` : 'All good',
      chartData: prepareRatingChartData(stats.ratingBreakdown || [])
    },
    uninspected: {
      id: 'uninspected_bridges', 
      title: "Uninspected Bridges", 
      count: uninspected, 
      progress: totalBridges > 0 ? Math.round((uninspected / totalBridges) * 100) : 0, 
      description: `Bridges pending initial inspection`,
      Icon: AssignmentLateIcon, 
      color: 'warning.main', 
      trend: uninspected > 0 ? 'Action needed' : 'Up to date',
      chartData: prepareInspectionStatusChartData(totalBridges, uninspected)
    },
    networkSevere: { 
      id: 'network_severe', 
      title: "Network Most Severe", 
      count: networkSevereCount, 
      progress: networkSevereCount > 0 ? Math.min((networkSevereCount / 5) * 100, 100) : 0, 
      description: stats.networkWithMostSevereBridges?.network_name || 'N/A',
      Icon: PlaceIcon, 
      color: 'secondary.main', 
      trend: networkSevereCount > 0 ? `${networkSevereCount} severe` : '0'
    },
    sectionSevere: { 
      id: 'section_severe', 
      title: "Section Most Severe", 
      count: sectionSevereCount, 
      progress: sectionSevereCount > 0 ? Math.min((sectionSevereCount / 5) * 100, 100) : 0, 
      description: stats.sectionWithMostSevereBridges?.section_name || 'N/A',
      Icon: LocationOnIcon, 
      color: 'secondary.main', 
      trend: sectionSevereCount > 0 ? `${sectionSevereCount} severe` : '0'
    },
  };
};

// Function to determine if a trend is positive or negative
const getTrendDirection = (trend) => {
  if (!trend) return 'neutral';
  return trend.startsWith('+') ? 'up' : trend.startsWith('-') ? 'down' : 'neutral';
};

const BridgeSummarySection = () => {
  const theme = useTheme();
  const { isAuthenticated, getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;
  
  const [stats, setStats] = useState({
    totalBridges: 0,
    bridgeTypeBreakdown: [],
    severeRatedBridges: 0,
    bridgesWithoutInspection: 0,
    networkWithMostSevereBridges: { network_name: 'N/A', severe_count: 0 },
    sectionWithMostSevereBridges: { section_name: 'N/A', severe_count: 0 },
    ratingBreakdown: []
  });
  
  // Additional state for chart data
  const [ratingBreakdown, setRatingBreakdown] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);
  
  // Table data state for expanded cards
  const [tableData, setTableData] = useState({});
  const [tableLoading, setTableLoading] = useState({});
  const [tableError, setTableError] = useState({});
  
  // Pagination state for each table
  const [tablePagination, setTablePagination] = useState({});
  const [tableTotalCounts, setTableTotalCounts] = useState({});

  const fetchBridgeStats = useCallback(async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🏗️ [BridgeSummarySection] Fetching bridge dashboard stats...');
      const response = await analyticsApi.getBridgeDashboardStats();
      
      const bridgeStats = response.data.data || {};
      
      setStats({
        totalBridges: bridgeStats.totalBridges || 0,
        bridgeTypeBreakdown: bridgeStats.bridgeTypeBreakdown || [],
        severeRatedBridges: bridgeStats.severeRatedBridges || 0,
        bridgesWithoutInspection: bridgeStats.bridgesWithoutInspection || 0,
        networkWithMostSevereBridges: bridgeStats.networkWithMostSevereBridges || { network_name: 'N/A', severe_count: 0 },
        sectionWithMostSevereBridges: bridgeStats.sectionWithMostSevereBridges || { section_name: 'N/A', severe_count: 0 },
        ratingBreakdown: bridgeStats.ratingBreakdown || []
      });
      
      console.log('✅ [BridgeSummarySection] Bridge stats loaded successfully:', bridgeStats);
      
    } catch (err) {
      console.error('❌ [BridgeSummarySection] Failed to fetch bridge stats:', err);
      setError('Failed to load bridge statistics');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchBridgeStats();
  }, [fetchBridgeStats]);

  // Generate bridge data from real stats
  const bridgeData = getBridgeDataFromStats(stats, loading);

  // Function to fetch table data for a specific card
  const fetchTableDataForCard = useCallback(async (cardId, page = 0, rowsPerPage = 10) => {
    if (!organizationId) return;

    setTableLoading(prev => ({ ...prev, [cardId]: true }));
    setTableError(prev => ({ ...prev, [cardId]: null }));

    try {
      // Get the actual API card type from the bridge data
      const apiCardType = bridgeData[cardId]?.id || cardId;
      console.log(`🏗️ [BridgeSummarySection] Fetching table data for card: ${cardId} (API: ${apiCardType})`);
      
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: 'created_at',
        sort_order: 'DESC'
      };

      const response = await analyticsApi.getBridgeDetails(apiCardType, params);
      const data = response.data.data || [];
      const total = response.data.total || 0;

      setTableData(prev => ({ ...prev, [cardId]: data }));
      setTableTotalCounts(prev => ({ ...prev, [cardId]: total }));
      
      console.log(`✅ [BridgeSummarySection] Table data loaded for ${cardId}: ${data.length} bridges, total: ${total}`);
      
    } catch (err) {
      console.error(`❌ [BridgeSummarySection] Failed to fetch table data for ${cardId}:`, err);
      setTableError(prev => ({ 
        ...prev, 
        [cardId]: `Failed to load ${cardId} data` 
      }));
    } finally {
      setTableLoading(prev => ({ ...prev, [cardId]: false }));
    }
  }, [organizationId, bridgeData]);

  const handleCardHover = (cardId) => {
    // Disable hover effects when any card is expanded to prevent table blinking
    if (!expandedCardId) {
      setHoveredCardId(cardId);
    }
  };

  const handleExpandClick = (cardId) => {
    const newExpandedId = expandedCardId === cardId ? null : cardId;
    setExpandedCardId(newExpandedId);
    
    // Initialize pagination state for new card
    if (newExpandedId && !tablePagination[cardId]) {
      setTablePagination(prev => ({
        ...prev,
        [cardId]: { page: 0, rowsPerPage: 10 }
      }));
    }
    
    // Fetch data when expanding a card
    if (newExpandedId && !tableData[cardId]) {
      const pagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
      fetchTableDataForCard(cardId, pagination.page, pagination.rowsPerPage);
    }
  };

  // Pagination handlers
  const handleChangePage = (cardId, newPage) => {
    const currentPagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
    const updatedPagination = { ...currentPagination, page: newPage };
    
    setTablePagination(prev => ({
      ...prev,
      [cardId]: updatedPagination
    }));
    
    fetchTableDataForCard(cardId, newPage, currentPagination.rowsPerPage);
  };

  const handleChangeRowsPerPage = (cardId, newRowsPerPage) => {
    const updatedPagination = { page: 0, rowsPerPage: newRowsPerPage };
    
    setTablePagination(prev => ({
      ...prev,
      [cardId]: updatedPagination
    }));
    
    fetchTableDataForCard(cardId, 0, newRowsPerPage);
  };

  const cardStyle = {
    p: 0,
    borderRadius: 2,
    minHeight: '160px', // Consistent minimum height like DefectSummarySection
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
    }
  };

  // Updated renderCard function matching DefectSummarySection pattern
  const renderCard = (cardId, data) => {
    const { title, count, progress, description, Icon, color, trend, slogan, breakdown, chartData } = data;
    const colorParts = color.split('.');
    const cardColor = theme.palette[colorParts[0]]?.[colorParts[1] || 'main'] || colorParts[0]; 
    const isExpanded = expandedCardId === cardId;
    const isHovered = hoveredCardId === cardId;
    const trendDirection = getTrendDirection(trend);
    
    return (
      <Paper 
        sx={{
          ...cardStyle
        }}
        onMouseEnter={() => handleCardHover(cardId)}
        onMouseLeave={() => handleCardHover(null)}
      >
          {/* Header section */}
          <Box 
            sx={{ 
              px: 2, 
              py: 1.5, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: alpha(cardColor, 0.15),
              bgcolor: alpha(cardColor, 0.03)
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{ 
                  bgcolor: alpha(cardColor, 0.1), 
                  color: cardColor,
                  width: 32, 
                  height: 32,
                  mr: 1.5
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {title}
              </Typography>
            </Box>
            
            {trend && (
              <Chip 
                size="small"
                label={trend} 
                icon={trendDirection === 'up' ? <ArrowUpwardIcon fontSize="small" /> : 
                       trendDirection === 'down' ? <ArrowDownwardIcon fontSize="small" /> : null}
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  bgcolor: alpha(trendDirection === 'up' ? theme.palette.success.main : 
                                trendDirection === 'down' ? theme.palette.error.main : 
                                theme.palette.grey[500], 0.1),
                  color: trendDirection === 'up' ? theme.palette.success.dark : 
                         trendDirection === 'down' ? theme.palette.error.dark : 
                         theme.palette.grey[700],
                  fontWeight: 600,
                  '& .MuiChip-icon': { fontSize: '0.7rem' }
                }}
              />
            )}
          </Box>
          
          {/* Count section */}
          <Box sx={{ px: 2, py: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography 
              variant="h2" 
              component="div" 
              sx={{ 
                fontWeight: 800, 
                mb: 0.5,
                backgroundImage: `linear-gradient(135deg, ${cardColor}, ${alpha(cardColor, 0.7)})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {typeof count === 'string' ? count : count.toLocaleString()}
            </Typography>
            
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            >
              {description}
            </Typography>
            


            {/* Donut chart for specific cards */}
            {chartData && chartData.length > 0 && (cardId === 'bridgeTypes' || cardId === 'severeBridges' || cardId === 'uninspected') && (
              <Box sx={{ mt: 0.5, height: 60, display: 'flex', alignItems: 'center' }}>
                <ResponsiveContainer width="40%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={12}
                      outerRadius={24}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name) => [`${value}`, name]}
                      labelStyle={{ fontSize: '0.7rem' }}
                      contentStyle={{
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                        border: `1px solid ${alpha(cardColor, 0.2)}`,
                        borderRadius: 4
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ ml: 1, flex: 1 }}>
                  {(cardId === 'bridgeTypes' ? chartData.slice(0, 2) : chartData).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.2 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          mr: 0.5,
                          flexShrink: 0
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', flex: 1 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.6rem' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                  {cardId === 'bridgeTypes' && chartData.length > 2 && (
                   <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', fontStyle: 'italic' }}>
                     +{chartData.length - 2} more
                   </Typography>
                 )}
                </Box>
              </Box>
            )}

            {/* Fallback breakdown for cards without charts */}
            {breakdown && breakdown.length > 0 && (!chartData || chartData.length === 0) && cardId !== 'bridgeTypes' && cardId !== 'severeBridges' && cardId !== 'uninspected' && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  Top Types:
                </Typography>
                <Box sx={{ mt: 0.25 }}>
                  {breakdown.slice(0, 3).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {item.subtype_name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                  {breakdown.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontStyle: 'italic', mt: 0.1 }}>
                      +{breakdown.length - 3} more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Progress bar section */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                bgcolor: alpha(cardColor, 0.15), 
                '& .MuiLinearProgress-bar': {
                  bgcolor: cardColor,
                  borderRadius: 3,
                }
              }} 
            />
          </Box>
          
          {/* Expand Button */}
          <Fade in={isHovered || isExpanded}>
            <IconButton
              onClick={() => handleExpandClick(cardId)}
              sx={{
                position: 'absolute',
                bottom: 6,
                right: 6,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                }
              }}
              size="small"
              aria-expanded={isExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Fade>
        </Paper>
    );
  };

  // Detail Table Component with Real Bridge Data
  const DetailTable = ({ cardId }) => {
    const data = tableData[cardId] || [];
    const loading = tableLoading[cardId] || false;
    const error = tableError[cardId] || null;
    const title = bridgeData[cardId]?.title || 'Bridge Details';
    const count = bridgeData[cardId]?.count || 0;
    const cardColor = bridgeData[cardId]?.color || 'grey.600';
    const colorParts = cardColor.split('.');
    const mainColor = theme.palette[colorParts[0]]?.[colorParts[1] || 'main'] || colorParts[0];
    
    // Get pagination state for this card
    const pagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
    const totalCount = tableTotalCounts[cardId] || 0;

    // Show loading state
    if (loading) {
      return (
        <Fade in={true} timeout={400}>
          <Paper sx={{ 
            mt: 2, 
            borderRadius: 2, 
            overflow: 'hidden', 
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${alpha(mainColor, 0.2)}`,
            p: 4,
            textAlign: 'center'
          }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading {title.toLowerCase()} data...
            </Typography>
          </Paper>
        </Fade>
      );
    }

    // Show error state
    if (error) {
      return (
        <Fade in={true} timeout={400}>
          <Paper sx={{ 
            mt: 2, 
            borderRadius: 2, 
            overflow: 'hidden', 
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            p: 4,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Try again
            </Typography>
          </Paper>
        </Fade>
      );
    }

    const getRatingChip = (rating) => {
      const ratingConfig = {
        'Severe': { color: 'error', bgColor: '#ffebee' },
        'Poor': { color: 'error', bgColor: '#ffebee' },
        'Fair': { color: 'warning', bgColor: '#fff3e0' },
        'Good': { color: 'success', bgColor: '#e8f5e8' },
        'Excellent': { color: 'success', bgColor: '#e8f5e8' },
        'Unrated': { color: 'default', bgColor: '#f5f5f5' }
      };
      
      const config = ratingConfig[rating.level] || ratingConfig['Unrated'];
      return (
        <Chip
          size="small"
          label={rating.level}
          sx={{
            backgroundColor: config.bgColor,
            color: theme.palette[config.color]?.dark || theme.palette.text.primary,
            fontWeight: 600,
            borderRadius: 1,
            fontSize: '0.7rem',
            height: 22
          }}
        />
      );
    };

    return (
      <Fade in={true} timeout={400}>
        <Paper sx={{ 
          mt: 2, 
          borderRadius: 2, 
          overflow: 'hidden', 
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${alpha(mainColor, 0.2)}`
        }}>
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(mainColor, 0.05),
            borderBottom: `1px solid ${alpha(mainColor, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Badge 
                badgeContent={count} 
                color={colorParts[0] !== 'grey' ? colorParts[0] : 'default'}
                sx={{ '& .MuiBadge-badge': { fontWeight: 600 } }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
              </Badge>
            </Stack>
            <Chip 
              label={`${data.length} of ${totalCount} items`} 
              size="small" 
              variant="outlined" 
              sx={{ borderRadius: 1.5 }}
            />
          </Box>
          
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Bridge ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Inspections</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Latest Inspection</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Coordinates</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((bridge, index) => (
                    <TableRow
                      key={`${bridge.id}-${index}`}
                      hover
                      sx={{
                        '&:hover td': { bgcolor: alpha(mainColor, 0.02) },
                        '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[50], 0.5) },
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(mainColor, 0.08),
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {bridge.bridgeId}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {bridge.name}
                        </Typography>
                        {bridge.code && (
                          <Typography variant="caption" color="text.secondary">
                            {bridge.code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {bridge.type}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {bridge.location}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getRatingChip(bridge.rating)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bridge.inspections.count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {bridge.inspections.latest}
                        </Typography>
                        {bridge.inspections.condition !== 'N/A' && (
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem' }}>
                            {bridge.inspections.condition}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {bridge.coordinates}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No {title.toLowerCase()} found
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Try refreshing or check if there are any bridges in your organization
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Sticky Pagination at bottom */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(event, newPage) => handleChangePage(cardId, newPage)}
            onRowsPerPageChange={(event) => handleChangeRowsPerPage(cardId, parseInt(event.target.value, 10))}
            showFirstButton
            showLastButton
            sx={{
              position: 'sticky',
              bottom: 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              zIndex: 1,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
            }}
          />
        </Paper>
      </Fade>
    );
  };

  // Don't render if not authenticated or no organization
  if (!isAuthenticated || !organizationId) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {!isAuthenticated ? 'Authentication required' : 'No organization access'}
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Unable to load bridge statistics
        </Typography>
      </Box>
    );
  }

  return (
    <Box> 
      {/* Header with refresh info */}
      {!loading && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Real-time bridge infrastructure data • Last updated: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      )}

      <Box 
        sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)', 
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(6, 1fr)' 
          },
          gap: 2,
          mb: 3
        }}
      > 
        {Object.entries(bridgeData).map(([key, data]) => (
          <Box key={key}>
            {renderCard(key, data)}
          </Box>
        ))}
      </Box>
      
      <Collapse in={Boolean(expandedCardId)} timeout="auto" unmountOnExit>
        {expandedCardId && <DetailTable cardId={expandedCardId} />}
      </Collapse>
    </Box>
  );
};

export default BridgeSummarySection;